/**
 * Privilege Service
 * 
 * This service manages role-based permissions and privileges
 * It provides a central place to check, get, and set permissions
 * It uses localStorage for persistent storage
 */

import { getUserRole } from './authService';

// Default roles and their inherent access levels
const ROLES = {
  ADMIN: 'ADMIN',
  MANAGER: 'MANAGER',
  EXECUTIVE: 'EXECUTIVE',
  BILLING_PERSON: 'BILLING_PERSON',
  USER: 'USER'
};

// Default menu structure
const MENUS = {
  DASHBOARD: '/dashboard',
  INVENTORY: '/inventory',
  INVENTORY_CATEGORIES: '/inventory/categories',
  INVENTORY_PRODUCTS: '/inventory/products',
  INVENTORY_TAXES: '/inventory/taxes',
  INVENTORY_UNITS: '/inventory/units',
  SALES: '/sales',
  SALES_ORDERS: '/sales/pos',
  SALES_VIEW_ORDERS: '/sales/view-orders',
  SALES_RETURNS: '/sales/returns',
  SALES_VIEW_RETURNS: '/sales/view-returns',
  PURCHASES: '/purchases',
  PURCHASES_ORDERS: '/purchases/orders',
  PURCHASES_VIEW_ORDERS: '/purchases/view-orders',
  PURCHASES_RETURNS: '/purchases/returns',
  PEOPLE: '/people',
  PEOPLE_CUSTOMERS: '/people/customers',
  PEOPLE_SUPPLIERS: '/people/suppliers',
  PEOPLE_USERS: '/people/users',
  REPORTS: '/reports',
  REPORTS_SALES: '/reports/sales',
  REPORTS_PURCHASES: '/reports/purchases',
  REPORTS_INVENTORY: '/reports/inventory',
  REPORTS_PRODUCTS: '/reports/products',
  REPORTS_BILLING: '/reports/billing',
  REPORTS_PROFIT_LOSS: '/reports/profit-loss',
  REPORTS_CREDIT_CUSTOMERS: '/reports/credit-customers',
  PRIVILEGES: '/privileges',
  ROLE_PRIVILEGES: '/role-privileges',
  SETTINGS: '/settings'
};

// Default privileges for each role if none are defined
const DEFAULT_PRIVILEGES = {
  [ROLES.ADMIN]: {
    // Admin has access to everything
    visibleMenus: Object.keys(MENUS),
    accessibleRoutes: Object.values(MENUS).concat(
      ['/sales/*', '/purchases/*', '/inventory/*', '/people/*', '/reports/*', '/role-privileges']
    )
  },
  [ROLES.MANAGER]: {
    visibleMenus: [
      'DASHBOARD', 'INVENTORY', 'INVENTORY_CATEGORIES', 'INVENTORY_PRODUCTS', 
      'INVENTORY_TAXES', 'INVENTORY_UNITS', 'SALES', 'SALES_ORDERS', 
      'SALES_VIEW_ORDERS', 'SALES_RETURNS', 'SALES_VIEW_RETURNS', 
      'PURCHASES', 'PURCHASES_ORDERS', 'PURCHASES_VIEW_ORDERS', 'PURCHASES_RETURNS',
      'PEOPLE', 'PEOPLE_CUSTOMERS', 'PEOPLE_SUPPLIERS', 
      'REPORTS', 'REPORTS_SALES', 'REPORTS_PURCHASES', 'REPORTS_INVENTORY', 
      'REPORTS_PRODUCTS', 'REPORTS_BILLING'
    ],
    accessibleRoutes: [
      '/dashboard', '/inventory', '/inventory/*', '/sales', '/sales/*',
      '/purchases', '/purchases/*', '/people/customers', '/people/suppliers',
      '/reports', '/reports/*'
    ]
  },
  [ROLES.EXECUTIVE]: {
    visibleMenus: [
      'DASHBOARD', 
      'SALES', 'SALES_ORDERS', 'SALES_VIEW_ORDERS', 'SALES_RETURNS', 'SALES_VIEW_RETURNS',
      'PEOPLE', 'PEOPLE_CUSTOMERS',
      'INVENTORY', 'INVENTORY_PRODUCTS',
      'REPORTS', 'REPORTS_SALES', 'REPORTS_PRODUCTS'
    ],
    accessibleRoutes: [
      '/dashboard', 
      '/sales', '/sales/pos', '/sales/view-orders', '/sales/returns', '/sales/view-returns', '/sales/*',
      '/people', '/people/customers',
      '/inventory/products',
      '/reports/sales', '/reports/products'
    ]
  },
  [ROLES.BILLING_PERSON]: {
    visibleMenus: [
      'DASHBOARD', 'SALES', 'SALES_ORDERS', 'SALES_VIEW_ORDERS', 
      'REPORTS', 'REPORTS_SALES', 'REPORTS_BILLING', 'REPORTS_PRODUCTS'
    ],
    accessibleRoutes: [
      '/dashboard', '/sales/pos', '/sales/view-orders', 
      '/reports/sales', '/reports/billing', '/reports/products'
    ]
  },
  [ROLES.USER]: {
    visibleMenus: ['DASHBOARD'],
    accessibleRoutes: ['/dashboard']
  }
};

/**
 * Get the privileges for a specific role
 * @param {string} role - The role to get privileges for
 * @returns {Object} The privileges for the role
 */
export const getRolePrivileges = (role) => {
  // Admin always has full access
  if (role === ROLES.ADMIN) {
    return DEFAULT_PRIVILEGES[ROLES.ADMIN];
  }
  
  try {
    // Try to get stored privileges
    const storedPrivileges = localStorage.getItem(`privileges_${role}`);
    
    if (storedPrivileges) {
      return JSON.parse(storedPrivileges);
    }
    
    // If no stored privileges, use defaults and save them
    const defaultPrivileges = DEFAULT_PRIVILEGES[role] || DEFAULT_PRIVILEGES[ROLES.USER];
    localStorage.setItem(`privileges_${role}`, JSON.stringify(defaultPrivileges));
    
    return defaultPrivileges;
  } catch (error) {
    console.error('Error getting role privileges:', error);
    // Fallback to minimal privileges
    return { visibleMenus: ['DASHBOARD'], accessibleRoutes: ['/dashboard'] };
  }
};

/**
 * Save privileges for a specific role
 * @param {string} role - The role to save privileges for
 * @param {Object} privileges - The privileges to save
 * @returns {boolean} Whether the save was successful
 */
export const saveRolePrivileges = (role, privileges) => {
  try {
    localStorage.setItem(`privileges_${role}`, JSON.stringify(privileges));
    return true;
  } catch (error) {
    console.error('Error saving role privileges:', error);
    return false;
  }
};

/**
 * Check if the current user has access to a given route
 * @param {string} route - The route to check access for
 * @returns {boolean} Whether the user has access to the route
 */
export const hasRouteAccess = (route) => {
  try {
  const role = getUserRole();
  
  // Admin has access to everything
  if (role === ROLES.ADMIN) {
    return true;
  }
  
  // Get privileges for the current role
  const { accessibleRoutes = [] } = getRolePrivileges(role) || {};
  
    // Get submenu-specific permissions
    const submenuPermissions = getSubmenuPermissions(role);
    const submenuMap = submenuPermissions.reduce((map, submenu) => {
      map[submenu.id] = submenu;
      return map;
    }, {});
    
    // First, check exact route match
    if (accessibleRoutes.includes(route)) {
      return true;
    }
    
    // Check wildcard paths
    for (const accessibleRoute of accessibleRoutes) {
      if (accessibleRoute.endsWith('/*')) {
        const basePath = accessibleRoute.slice(0, -2);
        if (route.startsWith(basePath)) {
          // For wildcard paths, also check submenu permissions
          // Map route to menu ID
          const submenuId = Object.entries(MENUS).find(([key, path]) => 
            path === route
          )?.[0];
          
          if (submenuId && submenuMap[submenuId]) {
            // If we have specific submenu permissions, use them
            return submenuMap[submenuId].canView;
          }
          
          // If no specific submenu permission is found, allow access based on parent
          return true;
        }
      }
    }
    
    return false;
  } catch (error) {
    console.error('Error checking route access:', error);
    return false;
  }
};

/**
 * Check if the current user has access to a specific API endpoint
 * @param {string} url - The API endpoint to check
 * @returns {boolean} Whether the user has access
 */
export const hasApiAccess = (url) => {
  // Skip permission check for auth-related endpoints
  if (url.includes('/auth/') || url.includes('/login')) {
    return true;
  }
  
  const role = getUserRole();
  
  // Admin has access to everything
  if (role === ROLES.ADMIN) {
    return true;
  }
  
  // Always allow dashboard data
  if (url.includes('/dashboard') || url.includes('/analytics')) {
    return true;
  }
  
  // Get privileges for the current role
  const { accessibleRoutes = [] } = getRolePrivileges(role) || {};
  
  // Map API endpoint to frontend routes
  const apiPathSegments = url.split('/').filter(Boolean);
  let resourceType = '';
  
  if (apiPathSegments.length > 0) {
    // Skip 'api' prefix if present
    const startIndex = apiPathSegments[0] === 'api' ? 1 : 0;
    
    if (apiPathSegments.length > startIndex) {
      resourceType = apiPathSegments[startIndex];
    }
  }
  
  // Add specific check for products - executives need to view products
  if (resourceType === 'products' && role === ROLES.EXECUTIVE) {
    return true;
  }
  
  // Resource-specific checks
  switch (resourceType) {
    case 'sales':
    case 'sales-orders':
      return accessibleRoutes.some(route => 
        route.startsWith('/sales') || route === '/sales' || route === '/sales/*'
      );
      
    case 'purchases':
    case 'purchase-orders':
      return accessibleRoutes.some(route => 
        route.startsWith('/purchases') || route === '/purchases' || route === '/purchases/*'
      );
      
    case 'inventory':
    case 'products':
    case 'categories':
    case 'taxes':
    case 'units':
      return accessibleRoutes.some(route => 
        route.startsWith('/inventory') || route === '/inventory' || route === '/inventory/*'
      );
      
    case 'people':
    case 'users':
    case 'customers':
    case 'suppliers':
      return accessibleRoutes.some(route => 
        route.startsWith('/people') || route === '/people' || route === '/people/*'
      );
      
    case 'reports':
      return accessibleRoutes.some(route => 
        route.startsWith('/reports') || route === '/reports' || route === '/reports/*'
      );
      
    default:
      return false;
  }
};

/**
 * Get the visible menu items for the current user
 * @returns {Array} The visible menu IDs
 */
export const getVisibleMenuItems = () => {
  try {
    const role = getUserRole();
    
    // Admin sees everything
    if (role === ROLES.ADMIN) {
      return Object.keys(MENUS);
    }
    
    // Get privileges for the current role
    const { visibleMenus = [] } = getRolePrivileges(role) || {};
    
    // Get submenu-specific permissions
    const submenuPermissions = getSubmenuPermissions(role);
    
    // If we have submenu permissions, filter based on them
    if (submenuPermissions.length > 0) {
      // Create a lookup map for quick access
      const submenuMap = submenuPermissions.reduce((map, submenu) => {
        map[submenu.id] = submenu.canView;
        return map;
      }, {});
      
      // Filter visible menus based on submenu permissions
      return visibleMenus.filter(menuId => {
        // For submenus, check the specific permission
        if (submenuMap.hasOwnProperty(menuId)) {
          return submenuMap[menuId];
        }
        
        // For main menus, check if any of their submenus are visible
        if (['INVENTORY', 'SALES', 'PURCHASES', 'REPORTS', 'PEOPLE'].includes(menuId)) {
          // Get submenu IDs for this menu
          const submenus = Object.keys(MENUS).filter(id => 
            id.startsWith(menuId + '_') || 
            (menuId === 'PEOPLE' && id.startsWith('PEOPLE_'))
          );
          
          // Check if any submenu is visible
          return submenus.some(submenuId => submenuMap[submenuId]);
        }
        
        // For other menus, use the original visibility
        return true;
      });
    }
    
    return visibleMenus;
  } catch (error) {
    console.error('Error getting visible menu items:', error);
    return [];
  }
};

/**
 * Initialize privileges system
 * This should be called when the app starts
 */
export const initializePrivileges = () => {
  // Clear existing privileges to force refresh with new menu items
  Object.keys(ROLES).forEach(role => {
    localStorage.removeItem(`privileges_${role}`);
  });

  // Ensure all roles have their default privileges
  Object.keys(ROLES).forEach(role => {
    if (!localStorage.getItem(`privileges_${role}`)) {
      localStorage.setItem(
        `privileges_${role}`, 
        JSON.stringify(DEFAULT_PRIVILEGES[role] || DEFAULT_PRIVILEGES[ROLES.USER])
      );
    }
  });
  
  console.log('Privileges system initialized');
};

/**
 * Save submenu-specific permissions to localStorage
 * This will allow granular control at the submenu level even though 
 * the backend only supports module-level permissions
 * 
 * @param {string} role - Role to save permissions for
 * @param {Array} submenuPermissions - Array of submenu permission objects
 */
export const saveSubmenuPermissions = (role, submenuPermissions) => {
  try {
    const key = `submenu_permissions_${role}`;
    localStorage.setItem(key, JSON.stringify(submenuPermissions));
    return true;
  } catch (error) {
    console.error('Error saving submenu permissions to localStorage:', error);
    return false;
  }
};

/**
 * Get submenu-specific permissions from localStorage
 * 
 * @param {string} role - Role to get permissions for
 * @returns {Array} Array of submenu permission objects
 */
export const getSubmenuPermissions = (role) => {
  try {
    const key = `submenu_permissions_${role}`;
    const permissions = localStorage.getItem(key);
    return permissions ? JSON.parse(permissions) : [];
  } catch (error) {
    console.error('Error getting submenu permissions from localStorage:', error);
    return [];
  }
};

export const clearAndReinitializePrivileges = () => {
  // Clear all role-specific privileges from localStorage
  Object.values(ROLES).forEach(role => {
    localStorage.removeItem(`privileges_${role}`);
  });
  
  // Reinitialize with default privileges
  initializePrivileges();
};

export default {
  ROLES,
  MENUS,
  getRolePrivileges,
  saveRolePrivileges,
  hasRouteAccess,
  hasApiAccess,
  getVisibleMenuItems,
  initializePrivileges,
  clearAndReinitializePrivileges,
  saveSubmenuPermissions,
  getSubmenuPermissions
}; 